// Types for the Daily Intake / Retry Console / Patient Registry pages.
// Shapes mirror the backend endpoints in `backend/main.py`:
//   GET  /api/dashboard/intake/days
//   GET  /api/dashboard/intake/day
//   GET  /api/dashboard/patients
//   GET  /api/dashboard/patients/{patient_key}
//   POST /api/dashboard/retry/preflight
//   POST /api/dashboard/retry/execute

export type FailClass =
  | "live_outcome"
  | "not_submitted"
  | "in_progress"
  | "pending_verification"
  | "pending"
  | "telephony_error"
  | "no_dialable_phone"
  | "wrong_number_retryable"
  | "retryable_other"
  | "exhausted_live_agent"
  | "exhausted_other"
  | "other";

/** One row per local-tz date returned by `/api/dashboard/intake/days`. */
export interface IntakeDay {
  date: string;
  total: number;
  completed: number;
  failed: number;
  in_flight: number;
  retryable: number;
  exhausted: number;
  duplicates_seen_before: number;
  duplicates_within_day: number;
  fail_classes: Partial<Record<FailClass, number>>;
  /**
   * Display-only breakdown of the ``live_outcome`` (success) bucket by BV
   * ``success_category``. Used to split "Success" into BV Obtained / Tier-2 /
   * Inactive panes; does not affect classification or the success total.
   */
  success_categories?: Partial<Record<string, number>>;
}

export interface IntakeDaysResponse {
  days: IntakeDay[];
  tz: string;
  lookback: number;
  case_count: number;
}

export type IntakeCaseType = "pa_status" | "benefits_verification";

/** BV structured-output success categories (when ``success`` is true). */
export type BvSuccessCategory =
  | "coverage_info_completely_obtained"
  | "inactive_insurance"
  | "inactive_insurance_ivr"
  | "inactive_insurance_live"
  | "patient_unverified_non_matching_ivr"
  | "patient_unverified_non_matching_live"
  | "patient_unverified_non_matching"
  | "patient_not_found_ivr"
  | "patient_not_found_live"
  | "patient_not_found"
  | "patient_unverified_missing_info_ivr"
  | "patient_unverified_missing_info_live"
  | "patient_unverified_missing_info"
  | "provider_unverified_non_matching_ivr"
  | "provider_unverified_non_matching_live"
  | "provider_unverified_non_matching"
  | "provider_unverified_missing_info_ivr"
  | "provider_unverified_missing_info_live"
  | "provider_unverified_missing_info";

/** One field where submitted member data disagrees with Stedi's returned subscriber. */
export interface StediSubscriberDiscrepancy {
  field: string;
  label: string;
  submitted: string;
  returned: string;
}

/** Operator-initial vs effective call value for one overwritable subscriber field. */
export interface StediSubscriberFieldOverlay {
  field: string;
  label: string;
  submitted: string;
  returned: string;
  is_discrepancy: boolean;
}

/** AAA or top-level error returned on a Stedi eligibility response. */
export interface StediAaaError {
  field?: string;
  code?: string;
  description?: string;
  followup_action?: string;
  location?: string;
  /** Normalized from Stedi ``possibleResolutions`` when persisted. */
  possible_resolutions?: string;
}

/** Inactive benefit row from Stedi ``benefitsInformation`` (code 6). */
export interface StediInactiveBenefit {
  code: string;
  name: string;
  service_type_codes: string[];
  service_types: string[];
}

/** Outcome + provenance of the insurance-discovery backup coverage lookup. */
export interface StediInsuranceDiscovery {
  status?: string;
  discovery_id?: string | null;
  coverages_found?: number;
  matched_party?: string;
  matched_reasons?: string[];
  matched_subscriber_name?: string | null;
  discovery_payer_name?: string | null;
  discovery_payer_id?: string | null;
  corrected_member_id?: {
    from?: string | null;
    to?: string | null;
    changed?: boolean;
  };
  rerun_usable?: boolean;
  attempted_at?: string;
  request_zip?: string | null;
}

/** Slim case shape used across intake / retry / patients pages. */
export interface IntakeCase {
  case_id: string;
  document_id: string;
  case_type: IntakeCaseType;
  created_at: string | null;
  local_date: string | null;
  updated_at: string | null;
  status: string;
  success: string;
  fail_class: FailClass;
  fail_reason: string;
  pa_status: string;
  /** BV structured output: type of successful outcome when ``success`` is true. */
  success_category: string;
  fail_details: string;
  patient_key: string | null;
  is_duplicate_patient: boolean;
  member_first_name: string;
  member_last_name: string;
  member_dob: string;
  member_id: string;
  patient_id: string;
  member_bin: string;
  member_pcn: string;
  patient_address: string;
  group_number: string;
  payer_name: string;
  structured_output_payer_name?: string | null;
  payer_phone_number: string;
  product_name: string;
  j_code: string;
  attempts: number;
  total_call_attempts: number;
  vapi_call_count: number;
  tried_phone_numbers: string[];
  available_phone_numbers: string[];
  all_numbers_exhausted: boolean;
  outbound_deferred_queued: boolean;
  outbound_scheduled_for: string | null;
  current_batch_id: string | null;
  alternate_contact_number: string | null;
  /**
   * Set when the webhook auto-retry loop paused this case after two consecutive
   * identical not_submitted_* outcomes. Operators can still manually retry from
   * the Retry Console; the value is informational (e.g. ``repeated_not_submitted_live``).
   */
  auto_retry_paused_reason: string | null;
  /** Provider that handled the most recent attempt. */
  voice_provider: VoiceProvider | null;
  /** One-shot override consumed on next retry; null after consumption. */
  voice_provider_override: VoiceProvider | null;
  /** True when Stedi returned subscriber demographics that differ from what we submitted. */
  stedi_has_subscriber_discrepancies: boolean;
  stedi_subscriber_discrepancies: StediSubscriberDiscrepancy[];
  /** Operator-initial vs effective call values for Stedi-overwritable fields. */
  stedi_subscriber_field_overlays: StediSubscriberFieldOverlay[];
  /** True when Stedi returned AAA errors on the eligibility response. */
  stedi_has_aaa_errors: boolean;
  stedi_aaa_errors: StediAaaError[];
  /** True when Stedi returned top-level eligibility errors (``status: ERROR``). */
  stedi_has_eligibility_errors: boolean;
  stedi_eligibility_errors: StediAaaError[];
  /** True when available phones were narrowed to Stedi-validated directory numbers. */
  stedi_payer_phones_filtered: boolean;
  /** Stedi eligibility block the validated dial number came from, when filtered. */
  stedi_payer_phone_source?: string | null;
  /** True when Stedi returned phones that do not match payerPhoneDirectory. */
  stedi_payer_phone_directory_mismatch: boolean;
  stedi_returned_phone_numbers: string[];
  payer_directory_phone_numbers: string[];
  /**
   * BCBS (subplan) state-aware routing. ``bcbs_routed_state`` is the home-plan
   * route a generic BCBS case was dialed on; ``bcbs_detected_state`` is set when
   * a home plan was detected but not dialable (unresolved).
   * ``bcbs_federal_reference_numbers`` is the state's Federal (FEP) line(s):
   * informational only, because a Federal member's state cannot be auto-detected.
   */
  bcbs_routed_state?: string | null;
  bcbs_detected_state?: string | null;
  bcbs_detected_home_plan?: string | null;
  bcbs_routed_matched_by?: string | null;
  bcbs_state_routing_unresolved?: boolean;
  bcbs_federal_reference_numbers?: string[];
  /** True when a non-Federal BCBS case dialed only the universal BlueCard catch-all. */
  bcbs_routed_via_catch_all?: boolean;
  /** True when Stedi eligibility returned inactive benefit coverage (code 6). */
  stedi_benefits_inactive: boolean;
  stedi_inactive_benefits: StediInactiveBenefit[];
  /** True when the full Stedi eligibility JSON is stored on the patient record. */
  has_stedi_eligibility_response: boolean;
  /** Stedi payer id used as tradingPartnerServiceId on the eligibility check. */
  stedi_trading_partner_service_id?: string | null;
  /**
   * Insurance-discovery backup outcome: MATCHED (member id repaired + re-run hit),
   * NO_MATCH / NO_USABLE_MATCH (nothing verified), NO_CHANGE (same id), or ERROR.
   */
  stedi_insurance_discovery_status?: string | null;
  stedi_insurance_discovery?: StediInsuranceDiscovery | null;
  /**
   * True when the case has a payer but no stored eligibility response — Stedi id
   * was not resolved (or eligibility never ran).
   */
  stedi_payer_id_missing: boolean;
  /** ``ENRICHED`` when Stedi has run for this case (may predate response storage). */
  stedi_enrichment_status?: string | null;
  /** True when required demographics exist for a manual Stedi rerun (even if a response is already stored). */
  stedi_rerun_eligible: boolean;
  /** True after the Medicare form-backend structured output flow completed. */
  medicare_structured_output_completed?: boolean;
  medicare_structured_output_queued?: boolean;
  medicare_structured_output_error?: string | null;
}

/** Lightweight KPI shape derived client-side from `IntakeDay`. */
export interface IntakeDayKpis {
  total: number;
  /** All successful calls (the ``live_outcome`` bucket). */
  completed: number;
  /** Tier-1 success: BV coverage fully obtained (``coverage_info_completely_obtained``). */
  bv_obtained: number;
  /** Tier-2 success: patient mismatch / not found / missing info. */
  tier2_success: number;
  /** Inactive insurance (its own tier-1 outcome, tracked separately). */
  inactive: number;
  not_submitted: number;
  in_flight: number;
  retryable: number;
  exhausted: number;
  no_dialable_phone: number;
  duplicate_patients: number;
}

/** Paginated /api/dashboard/intake/day response. */
export interface IntakeDayResponse {
  date: string;
  tz: string;
  cases: IntakeCase[];
  has_more: boolean;
  next_cursor: string | null;
  duplicate_lookback_used: number;
  duplicate_detection_applied: boolean;
}

export interface PatientGroup {
  patient_key: string;
  first_name: string;
  last_name: string;
  dob: string;
  case_count: number;
  first_seen_at: string | null;
  last_seen_at: string | null;
  first_seen_date: string | null;
  last_seen_date: string | null;
  member_ids: string[];
  payers: string[];
  products: string[];
  fail_classes: FailClass[];
  case_ids: string[];
  /** Any case for this patient had Stedi subscriber mismatches in the lookback window. */
  has_stedi_subscriber_discrepancies?: boolean;
  stedi_discrepancy_case_count?: number;
  has_stedi_aaa_errors?: boolean;
  stedi_aaa_error_case_count?: number;
  has_stedi_eligibility_errors?: boolean;
  stedi_eligibility_error_case_count?: number;
}

/** Case-level intake buckets for the patient registry lookback window (Daily Intake semantics). */
export interface IntakeWindowCaseKpis {
  total: number;
  completed: number;
  not_submitted: number;
  in_flight: number;
  retryable: number;
  exhausted: number;
  no_dialable_phone: number;
}

/** Patient registry: one bucket per patient from their most recent case in the window. */
export type IntakeWindowPatientKpis = IntakeWindowCaseKpis;

export interface PatientsResponse {
  patients: PatientGroup[];
  patient_count: number;
  returned_count: number;
  limit: number;
  offset: number;
  has_more: boolean;
  case_count: number;
  unkeyed_case_count: number;
  tz: string;
  lookback: number;
  window_case_kpis?: IntakeWindowCaseKpis;
  window_patient_kpis?: IntakeWindowPatientKpis;
}

export interface PatientDetailResponse {
  patient_key: string;
  first_name: string;
  last_name: string;
  dob: string;
  case_count: number;
  cases: IntakeCase[];
  tz: string;
  lookback: number;
}

export type VoiceProvider = "vapi" | "asepha_voice";

export interface RetryOverride {
  payer_phone_number?: string;
  available_phone_numbers?: string[];
  voice_provider?: VoiceProvider;
}

export interface RetryRequestBody {
  case_ids: string[];
  overrides?: Record<string, RetryOverride>;
  retry_product_name?: string;
  batch_size?: number;
  batch_offset_minutes?: number;
}

export interface RetryPhoneFields {
  payer_phone_number: string;
  available_phone_numbers: string[];
  tried_phone_numbers: string[];
  alternate_contact_number: string | null;
  all_numbers_exhausted: boolean;
  stedi_payer_phone_directory_mismatch?: boolean;
  stedi_returned_phone_numbers?: string[];
  payer_directory_phone_numbers?: string[];
}

export interface RetryReadyRow extends RetryPhoneFields {
  case_id: string;
  name: string;
  product_name: string;
  payer_name: string;
  fail_class: FailClass;
  fail_reason: string;
  attempts: number;
  current_voice_provider?: VoiceProvider | null;
  voice_provider_override?: VoiceProvider | null;
}

export interface RetryNeedsInputRow extends RetryReadyRow {
  reason: "needs_phone_number" | "stedi_payer_phone_directory_mismatch";
  missing_fields: string[];
  invalid_phone?: string | null;
  suggested_phone?: string | null;
}

export interface RetryCaseOverrides {
  payer_phone_number?: string;
  available_phone_numbers?: string[];
  voice_provider?: VoiceProvider;
}

export interface RetryBlockedRow {
  case_id: string;
  reason: string;
  detail?: string;
  vob_batch_marked_ready?: boolean;
  batch_id?: string;
  outbound_scheduled_for?: string | null;
  missing_fields?: string[];
  name?: string;
  payer_name?: string;
  payer_phone_number?: string;
  fail_class?: FailClass;
  fail_reason?: string;
  attempts?: number;
}

export interface RetryPreflightResponse {
  ready: RetryReadyRow[];
  needs_input: RetryNeedsInputRow[];
  blocked: RetryBlockedRow[];
  summary: {
    total: number;
    ready: number;
    needs_input: number;
    blocked: number;
  };
}

export interface RetryReleaseBatchHoldResult {
  case_id: string;
  ok: boolean;
  released?: boolean;
  batch_id?: string | null;
  case_removed?: boolean | null;
  still_in_active_batch?: boolean;
  detail?: string;
  error?: string;
}

export interface RetryReleaseBatchHoldResponse {
  results: RetryReleaseBatchHoldResult[];
}

export interface RetryReleaseSuccessHoldResult {
  case_id: string;
  ok: boolean;
  released?: boolean;
  removed_from_successful_calls?: boolean | null;
  still_successful?: boolean;
  detail?: string;
  error?: string;
}

export interface RetryReleaseSuccessHoldResponse {
  results: RetryReleaseSuccessHoldResult[];
}

export interface RetryExecuteResponse {
  success: boolean;
  message?: string;
  batch_id?: string;
  assistant_id?: string;
  cases_total?: number;
  cases_processed?: number;
  cases_created?: number;
  cases_skipped?: number;
  cases_invalid?: number;
  cases_errors?: number;
  errors?: { case_id?: string; error: string }[];
  invalid_cases?: { case_id?: string | null; missing_fields: string[] }[];
  retry_input_count?: number;
  retry_fetch_errors?: number;
  deferred_telephony?: boolean;
}

/** Display label for a persisted case type. */
export const CASE_TYPE_LABEL: Record<IntakeCaseType, string> = {
  pa_status: "PA",
  benefits_verification: "BV",
};

/** Tone for a case type when shown as a badge. */
export const CASE_TYPE_TONE: Record<
  IntakeCaseType,
  "success" | "failure" | "warning" | "info" | "neutral" | "muted"
> = {
  pa_status: "info",
  benefits_verification: "neutral",
};

/** Display label for a fail_class. */
export const FAIL_CLASS_LABEL: Record<FailClass, string> = {
  live_outcome: "Success",
  not_submitted: "Not submitted",
  in_progress: "In progress",
  pending_verification: "Pending verification",
  pending: "Pending",
  telephony_error: "Telephony error",
  no_dialable_phone: "No dialable phone",
  wrong_number_retryable: "Wrong number (retryable)",
  retryable_other: "Failed (retryable)",
  exhausted_live_agent: "Exhausted: live agent",
  exhausted_other: "Exhausted",
  other: "Other",
};

/** Tone for a fail_class when shown as a badge. */
export const FAIL_CLASS_TONE: Record<
  FailClass,
  "success" | "failure" | "warning" | "info" | "neutral" | "muted"
> = {
  live_outcome: "success",
  not_submitted: "warning",
  in_progress: "info",
  pending_verification: "warning",
  pending: "muted",
  telephony_error: "failure",
  no_dialable_phone: "failure",
  wrong_number_retryable: "warning",
  retryable_other: "failure",
  exhausted_live_agent: "failure",
  exhausted_other: "failure",
  other: "neutral",
};

/** Buckets that the user can retry with one click. */
export const RETRYABLE_FAIL_CLASSES: FailClass[] = [
  "telephony_error",
  "wrong_number_retryable",
  "retryable_other",
];

/** Matches intake KPI "In flight" (pending / active call / BIN–PCN verification). */
export const IN_FLIGHT_FAIL_CLASSES: FailClass[] = [
  "pending",
  "in_progress",
  "pending_verification",
];

/** Short display labels for BV success categories in intake tables. */
export const BV_SUCCESS_CATEGORY_LABEL: Record<BvSuccessCategory, string> = {
  coverage_info_completely_obtained: "Full coverage",
  inactive_insurance: "Inactive insurance",
  patient_unverified_non_matching: "Patient mismatch",
  patient_not_found: "Patient not found",
  patient_unverified_missing_info: "Missing patient info",
  inactive_insurance_ivr: "Inactive insurance (IVR)",
  inactive_insurance_live: "Inactive insurance (live agent)",
  patient_unverified_non_matching_ivr: "Patient mismatch (IVR)",
  patient_unverified_non_matching_live: "Patient mismatch (live agent)",
  patient_not_found_ivr: "Patient not found (IVR)",
  patient_not_found_live: "Patient not found (live agent)",
  patient_unverified_missing_info_ivr: "Missing patient info (IVR)",
  patient_unverified_missing_info_live: "Missing patient info (live agent)",
  provider_unverified_non_matching: "Provider mismatch",
  provider_unverified_missing_info: "Missing provider info",
  provider_unverified_non_matching_ivr: "Provider mismatch (IVR)",
  provider_unverified_non_matching_live: "Provider mismatch (live agent)",
  provider_unverified_missing_info_ivr: "Missing provider info (IVR)",
  provider_unverified_missing_info_live: "Missing provider info (live agent)"
};

/** Badge tone for BV success categories. */
export const BV_SUCCESS_CATEGORY_TONE: Record<
  BvSuccessCategory,
  "success" | "failure" | "warning" | "info" | "neutral" | "muted"
> = {
  coverage_info_completely_obtained: "success",
  inactive_insurance: "neutral",
  patient_unverified_non_matching: "warning",
  patient_not_found: "warning",
  patient_unverified_missing_info: "warning",
  inactive_insurance_ivr: "neutral",
  inactive_insurance_live: "neutral",
  patient_unverified_non_matching_ivr: "warning",
  patient_unverified_non_matching_live: "warning",
  patient_not_found_ivr: "warning",
  patient_not_found_live: "warning",
  patient_unverified_missing_info_ivr: "warning",
  patient_unverified_missing_info_live: "warning",
  provider_unverified_non_matching: "warning",
  provider_unverified_missing_info: "warning",
  provider_unverified_non_matching_ivr: "warning",
  provider_unverified_non_matching_live: "warning",
  provider_unverified_missing_info_ivr: "warning",
  provider_unverified_missing_info_live: "warning"
};

/**
 * Display-only grouping of BV ``success_category`` values into dashboard panes.
 * Mirrors the backend ``BV_TIER_1_SUCCESS_CATEGORIES`` split purely for the UI —
 * it does NOT change grading, tier logic, or anything consumed downstream.
 *
 *   - ``bv_obtained``: Tier-1 coverage fully obtained.
 *   - ``inactive``: inactive insurance (Tier-1, but surfaced as its own pane).
 *   - ``tier2``: Tier-2 successes (patient mismatch / not found / missing info).
 */
export type BvSuccessGroup = "bv_obtained" | "inactive" | "tier2";

export const BV_INACTIVE_SUCCESS_CATEGORIES: readonly BvSuccessCategory[] = [
  "inactive_insurance",
  "inactive_insurance_ivr",
  "inactive_insurance_live",
];

export const BV_TIER_2_SUCCESS_CATEGORIES: readonly BvSuccessCategory[] = [
  "patient_unverified_non_matching",
  "patient_unverified_non_matching_ivr",
  "patient_unverified_non_matching_live",
  "patient_not_found",
  "patient_not_found_ivr",
  "patient_not_found_live",
  "patient_unverified_missing_info",
  "patient_unverified_missing_info_ivr",
  "patient_unverified_missing_info_live",
  "provider_unverified_non_matching",
  "provider_unverified_non_matching_ivr",
  "provider_unverified_non_matching_live",
  "provider_unverified_missing_info",
  "provider_unverified_missing_info_ivr",
  "provider_unverified_missing_info_live",
];
